import { Alert, Button, PasswordInput, Stack, Text } from "@mantine/core";
import { schemaResolver, useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { z } from "zod";

import { errorMessage, isApiError } from "@/api";

import { useUpdateMeMutation } from "./api";

const schema = z
  .object({
    current: z.string().min(1, "Required"),
    password: z.string().min(8, "At least 8 characters"),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, { path: ["confirm"], message: "Passwords do not match" });

/** PATCH auth/me {current_password, new_password}. Used on /account and on the enrolment page. */
export function ChangePasswordForm() {
  const update = useUpdateMeMutation();
  const [error, setError] = useState<string | null>(null);
  const form = useForm({
    initialValues: { current: "", password: "", confirm: "" },
    validate: schemaResolver(schema, { sync: true }),
  });

  const submit = form.onSubmit(async (v) => {
    setError(null);
    try {
      await update.mutateAsync({ current_password: v.current, new_password: v.password });
      form.reset();
      notifications.show({ color: "green", message: "Password changed. Your other sessions were signed out." });
    } catch (e) {
      if (isApiError(e, "INVALID_CREDENTIALS")) form.setFieldError("current", "Current password is wrong");
      else setError(errorMessage(e));
    }
  });

  return (
    <form onSubmit={submit} data-testid="change-password-form">
      <Stack gap="sm" maw={420}>
        <Text size="sm" c="dimmed" data-testid="password-signout-note">
          Changing your password signs you out on every other device; this session stays signed in.
        </Text>
        <PasswordInput label="Current password" autoComplete="current-password" {...form.getInputProps("current")} />
        <PasswordInput label="New password" autoComplete="new-password" {...form.getInputProps("password")} />
        <PasswordInput label="Repeat new password" autoComplete="new-password" {...form.getInputProps("confirm")} />
        {error && <Alert color="red">{error}</Alert>}
        <div>
          <Button type="submit" variant="light" loading={update.isPending}>
            Change password
          </Button>
        </div>
      </Stack>
    </form>
  );
}
